// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import Authentication from '@app/features/auth/state/Authentication';
import * as HolidayCommands from '@app/features/holiday/commands/HolidayCommands';
import {HolidayFlybyFlock} from '@app/features/holiday/components/HolidayFlybyFlock';
import styles from '@app/features/holiday/components/HolidayLayer.module.css';
import {HolidayScare, prefetchHolidayScareArt} from '@app/features/holiday/components/HolidayScare';
import {HolidaySidePeek} from '@app/features/holiday/components/HolidaySidePeek';
import {registerBaselineSidePeekers} from '@app/features/holiday/packs/baseline/sidePeekers';
import CuteGhost from '@app/features/holiday/packs/halloween/assets/cute-ghost.svg?react';
import {registerHalloweenAvatarCostumes} from '@app/features/holiday/packs/halloween/avatarCostumes';
import {
	ambientFlybyGapMs,
	displayLoopDelayMs,
	firstAmbientFlybyGapMs,
	isHalloweenAmbientBatsEnabled,
	isHalloweenFlyingBatsForced,
} from '@app/features/holiday/packs/halloween/flyingBats';
import {registerHalloweenPeekingCats} from '@app/features/holiday/packs/halloween/peekingCats';
import {isHalloweenScaresEnabled} from '@app/features/holiday/packs/halloween/scares';
import {registerHalloweenSidePeekers} from '@app/features/holiday/packs/halloween/sidePeekers';
import Holiday, {
	type HolidayBatFlockParticle,
	type HolidayCuteGhostParticle,
	type HolidayParticle,
	type HolidayScareParticle,
	type HolidaySidePeekParticle,
} from '@app/features/holiday/state/Holiday';
import {heartColorForReaction} from '@app/features/holiday/utils/heartReactionColor';
import {getHolidayFlybyHost, subscribeHolidayFlybyHost} from '@app/features/holiday/utils/holidayFlybyHost';
import {getHolidayScareHost, subscribeHolidayScareHost} from '@app/features/holiday/utils/holidayScareHost';
import {isHolidaySeasonEnabled} from '@app/features/holiday/utils/holidaySeason';
import {getHolidaySidePeekHost, subscribeHolidaySidePeekHost} from '@app/features/holiday/utils/holidaySidePeekHost';
import {isUnicodeHeartReaction} from '@app/features/holiday/utils/isUnicodeHeartReaction';
import {type PostedMessageNotice, subscribePostedMessage} from '@app/features/messaging/utils/MessageCreateListeners';
import {subscribeMessageDelete} from '@app/features/messaging/utils/MessageDeleteListeners';
import {subscribeAddReaction} from '@app/features/messaging/utils/ReactionAddListeners';
import type {ReactionEmoji} from '@app/features/messaging/utils/ReactionUtils';
import {subscribeSpoilerReveal} from '@app/features/messaging/utils/SpoilerRevealListeners';
import SelectedChannel from '@app/features/navigation/state/SelectedChannel';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useRef, useSyncExternalStore} from 'react';
import {createPortal} from 'react-dom';

registerHalloweenAvatarCostumes();
registerHalloweenPeekingCats();
registerHalloweenSidePeekers();
registerBaselineSidePeekers();

const CUTE_GHOST_LIFETIME_MS = 1500;
const FORCE_FLYBY_GAP_MS = 1400;
/** How often an ambient scare is *rolled*. The season, chance, and cooldown gates live in the command. */
const AMBIENT_SCARE_ROLL_MS = 4 * 60 * 1000;

function onAddReaction(channelId: string, messageId: string, emoji: ReactionEmoji): void {
	if (isHolidaySeasonEnabled('halloween') && !Accessibility.useReducedMotion && isUnicodeHeartReaction(emoji)) {
		Holiday.armHeartColor(heartColorForReaction(emoji));
	}
	HolidayCommands.maybeSpawnHeartReactionGhost(channelId, messageId, emoji);
	HolidayCommands.maybeSpawnBatReactionFlock(channelId, messageId, emoji);
}

function onPostedMessage(notice: PostedMessageNotice): void {
	// Gateway MESSAGE_CREATE fires for every subscribed channel and every author. The scare is meant
	// for a message you just sent in the channel you are looking at: without the author check it
	// would fire on everyone else's messages, and without the channel check it would fire here when
	// you post from your phone or a second tab somewhere else.
	const isOwnMessage =
		Authentication.currentUserId != null && String(notice.authorId) === String(Authentication.currentUserId);
	const inCurrentChannel = notice.channelId === SelectedChannel.currentChannelId;
	if (isOwnMessage && inCurrentChannel) {
		HolidayCommands.maybeSpawnScare('send');
	}
	if (isHalloweenFlyingBatsForced() || Holiday.batsDisplayLoopEnabled) return;
	// Flybys answer anyone's message in the channel on screen, so the whole room sees the same bats.
	// The scare above stays own-message-only: it dims the chat, which should need your own action.
	HolidayCommands.maybeStartFlybyFromPostedMessage(notice.content, inCurrentChannel);
}

function onMessageDelete(notice: {channelId: string; messageId: string}): void {
	HolidayCommands.maybeSpawnSidePeek(notice.channelId, notice.messageId);
}

function onSpoilerReveal(notice: {channelId: string | null}): void {
	// Spoilers also render outside chat — profile bios, the What's New modal — where there is no
	// channel at all. Those must not fire a scare behind a popout, so a missing channel counts as
	// "not the visible one" rather than passing the check.
	if (notice.channelId == null || notice.channelId !== SelectedChannel.currentChannelId) return;
	HolidayCommands.maybeSpawnScare('spoiler-reveal');
}

const HolidayFlybyTriggers = observer(function HolidayFlybyTriggers(): null {
	const channelId = SelectedChannel.currentChannelId;
	const previousChannelId = useRef<string | null | undefined>(undefined);
	const ambientEnabled = isHalloweenAmbientBatsEnabled();
	const scaresEnabled = isHalloweenScaresEnabled();
	const reducedMotion = Accessibility.useReducedMotion;
	const artLoop = isHalloweenFlyingBatsForced();
	const displayLoop = Holiday.batsDisplayLoopEnabled;

	useEffect(() => subscribePostedMessage(onPostedMessage), []);

	useEffect(() => {
		if (previousChannelId.current === undefined) {
			previousChannelId.current = channelId;
			return;
		}
		if (previousChannelId.current === channelId) return;
		previousChannelId.current = channelId;
		HolidayCommands.maybeSpawnScare('channel-switch');
		if (artLoop || displayLoop) return;
		HolidayCommands.maybeStartFlybyFromChannelSwitch();
	}, [artLoop, channelId, displayLoop]);

	// The scare art is a lazy chunk, so fetch it as soon as scares are possible rather than when one
	// fires. Gated on the same condition as the ambient roll, so it stays off the wire out of season.
	useEffect(() => {
		if (reducedMotion || !scaresEnabled) return;
		prefetchHolidayScareArt();
	}, [reducedMotion, scaresEnabled]);

	// Ambient scares roll on their own timer. The command owns every gate, so this only decides how
	// often to ask. The deadline is kept in wall clock: alt-tabbing pauses the timer without pushing
	// the next roll further away, which would otherwise starve the roll for anyone who switches
	// windows more often than the interval.
	useEffect(() => {
		if (reducedMotion || !scaresEnabled) return;
		let timeoutId = 0;
		let nextRollAt = Date.now() + AMBIENT_SCARE_ROLL_MS;
		const arm = (): void => {
			window.clearTimeout(timeoutId);
			timeoutId = window.setTimeout(
				() => {
					HolidayCommands.maybeSpawnScare('ambient');
					nextRollAt = Date.now() + AMBIENT_SCARE_ROLL_MS;
					arm();
				},
				Math.max(0, nextRollAt - Date.now()),
			);
		};
		const onVisibility = (): void => {
			if (document.hidden) {
				window.clearTimeout(timeoutId);
				timeoutId = 0;
				return;
			}
			arm();
		};
		if (typeof document === 'undefined' || !document.hidden) arm();
		document.addEventListener('visibilitychange', onVisibility);
		return () => {
			window.clearTimeout(timeoutId);
			document.removeEventListener('visibilitychange', onVisibility);
		};
	}, [reducedMotion, scaresEnabled]);

	useEffect(() => {
		if (artLoop || displayLoop || !ambientEnabled || reducedMotion) return;
		let timeoutId = 0;
		// `first` is the arm right after mount or after the tab comes back, which gets a short delay
		// instead of a full season gap. Steady-state rolls use the season gap.
		const schedule = (first: boolean): void => {
			if (typeof document !== 'undefined' && document.hidden) return;
			const now = new Date();
			const delay = first ? firstAmbientFlybyGapMs(now, Math.random()) : ambientFlybyGapMs(now);
			timeoutId = window.setTimeout(() => {
				HolidayCommands.tryStartHolidayFlyby('ambient');
				schedule(false);
			}, delay);
		};
		const onVisibility = (): void => {
			window.clearTimeout(timeoutId);
			timeoutId = 0;
			if (!document.hidden) schedule(true);
		};
		schedule(true);
		document.addEventListener('visibilitychange', onVisibility);
		return () => {
			window.clearTimeout(timeoutId);
			document.removeEventListener('visibilitychange', onVisibility);
		};
	}, [ambientEnabled, artLoop, displayLoop, reducedMotion]);

	return null;
});

function CuteGhostSprite({particle}: {particle: HolidayCuteGhostParticle}): React.ReactElement {
	useEffect(() => {
		const timeoutId = window.setTimeout(() => {
			Holiday.removeParticle(particle.id);
		}, CUTE_GHOST_LIFETIME_MS);
		return () => window.clearTimeout(timeoutId);
	}, [particle.id]);

	return (
		<CuteGhost
			aria-hidden="true"
			className={styles.heartGhost}
			style={
				{
					left: particle.x,
					top: particle.y,
					'--ghost-heart': particle.heartColor,
				} as React.CSSProperties
			}
			data-flx="holiday.holiday-layer.heart-ghost"
		/>
	);
}

function BatsDisplayLoop(): null {
	useEffect(() => {
		let cancelled = false;
		let timeoutId = 0;
		const spawn = (): void => {
			if (cancelled) return;
			const beforeIds = new Set(Holiday.particles.map((particle) => particle.id));
			HolidayCommands.tryStartHolidayFlyby('debug-bats-display');
			let holdMs = 0;
			for (const particle of Holiday.particles) {
				if (particle.kind !== 'bat-flock' || beforeIds.has(particle.id)) continue;
				holdMs = Math.max(holdMs, particle.durationMs + (particle.startDelayMs ?? 0));
			}
			timeoutId = window.setTimeout(spawn, displayLoopDelayMs(holdMs, Math.random()));
		};
		spawn();
		return () => {
			cancelled = true;
			window.clearTimeout(timeoutId);
		};
	}, []);
	return null;
}

function ForcedFlyingBatsLoop(): null {
	useEffect(() => {
		let cancelled = false;
		let timeoutId = 0;
		const spawn = (): void => {
			if (cancelled) return;
			const id = HolidayCommands.tryStartHolidayFlyby('debug-bats-full');
			const particle = Holiday.particles.find((item) => item.id === id);
			const durationMs = particle?.kind === 'bat-flock' ? particle.durationMs : 4000;
			timeoutId = window.setTimeout(spawn, durationMs + FORCE_FLYBY_GAP_MS);
		};
		spawn();
		return () => {
			cancelled = true;
			window.clearTimeout(timeoutId);
		};
	}, []);
	return null;
}

function renderParticle(particle: HolidayParticle): React.ReactElement | null {
	switch (particle.kind) {
		case 'cute-ghost':
			return (
				<CuteGhostSprite
					key={particle.id}
					particle={particle}
					data-flx="holiday.holiday-layer.render-particle.cute-ghost-sprite"
				/>
			);
		case 'bat-flock':
			return (
				<HolidayFlybyFlock
					key={particle.id}
					particle={particle}
					data-flx="holiday.holiday-layer.render-particle.holiday-flyby-flock"
				/>
			);
		case 'side-peek':
			return (
				<HolidaySidePeek
					key={particle.id}
					particle={particle}
					data-flx="holiday.holiday-layer.render-particle.holiday-side-peek"
				/>
			);
		case 'scare':
			return (
				<HolidayScare
					key={particle.id}
					particle={particle}
					data-flx="holiday.holiday-layer.render-particle.holiday-scare"
				/>
			);
		default:
			return null;
	}
}

export const HolidayLayer = observer((): React.ReactElement => {
	const channelId = SelectedChannel.currentChannelId;
	useEffect(() => subscribeAddReaction(onAddReaction), []);
	useEffect(() => subscribeMessageDelete(onMessageDelete), []);
	useEffect(() => subscribeSpoilerReveal(onSpoilerReveal), []);
	useEffect(() => {
		return () => {
			Holiday.removeKind('side-peek');
		};
	}, [channelId]);
	const flybyHost = useSyncExternalStore(subscribeHolidayFlybyHost, getHolidayFlybyHost, () => null);
	const sidePeekHost = useSyncExternalStore(subscribeHolidaySidePeekHost, getHolidaySidePeekHost, () => null);
	const scareHost = useSyncExternalStore(subscribeHolidayScareHost, getHolidayScareHost, () => null);

	const ghosts: Array<HolidayCuteGhostParticle> = [];
	const crossFlocks: Array<HolidayBatFlockParticle> = [];
	const riseFlocks: Array<HolidayBatFlockParticle> = [];
	const sidePeeks: Array<HolidaySidePeekParticle> = [];
	const scares: Array<HolidayScareParticle> = [];
	// Switch on kind first, exhaustively. Each host renders a different layer, so a new kind that
	// fell through to another kind's bucket would silently mount at the wrong z-index.
	for (const particle of Holiday.particles) {
		switch (particle.kind) {
			case 'cute-ghost':
				ghosts.push(particle);
				break;
			case 'side-peek':
				if (particle.channelId === channelId) sidePeeks.push(particle);
				break;
			case 'bat-flock':
				if (particle.path === 'rise') riseFlocks.push(particle);
				else crossFlocks.push(particle);
				break;
			case 'scare':
				// Deliberately not channel-filtered: a scare runs out its lifetime across a switch.
				scares.push(particle);
				break;
			default: {
				const unhandled: never = particle;
				throw new Error(`Unhandled holiday particle kind: ${JSON.stringify(unhandled)}`);
			}
		}
	}

	const crossLayer = (
		<div
			className={styles.layer}
			data-overlay-pass-through="true"
			aria-hidden="true"
			data-flx="holiday.holiday-layer.flybys"
		>
			{isHalloweenFlyingBatsForced() ? (
				<ForcedFlyingBatsLoop data-flx="holiday.holiday-layer.forced-flying-bats-loop" />
			) : null}
			{Holiday.batsDisplayLoopEnabled ? <BatsDisplayLoop data-flx="holiday.holiday-layer.bats-display-loop" /> : null}
			{crossFlocks.map(renderParticle)}
		</div>
	);

	return (
		<>
			<HolidayFlybyTriggers data-flx="holiday.holiday-layer.holiday-flyby-triggers" />
			<div
				className={styles.layer}
				data-overlay-pass-through="true"
				aria-hidden="true"
				data-flx="holiday.holiday-layer.flybys-rise"
			>
				{riseFlocks.map(renderParticle)}
			</div>
			{flybyHost ? createPortal(crossLayer, flybyHost) : crossLayer}
			{sidePeekHost ? createPortal(sidePeeks.map(renderParticle), sidePeekHost) : null}
			{scareHost ? createPortal(scares.map(renderParticle), scareHost) : null}
			{createPortal(ghosts.map(renderParticle), document.body)}
		</>
	);
});
